import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import { getCrmBrandTradeLicenseMappingsEndpointIdentifier } from './use-get-crm-brand-trade-license-mappings-api';

interface IUpdateMappingLicensePayload {
  licenseNumber?: string;
  licenseConfidence?: number;
}

interface UpdateMappingLicenseVariables {
  id: number;
  data: IUpdateMappingLicensePayload;
}

export function useUpdateMappingLicenseApi(options?: any) {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, data }: UpdateMappingLicenseVariables): Promise<void> => {
    try {
      const response = await http.put(`ums/v1/crm/management/brands/trade-license/mappings/${id}/license`, data);
      return response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        title: 'Error Updating License Mapping',
        description:
          error.response.data.message || 'Failed to update license mapping. Please check your data and try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      throw error;
    }
  };

  return useMutationWrapper<void, AxiosError, UpdateMappingLicenseVariables>(mutationFn, {
    onSuccess: (_data) => {
      toast({
        title: 'License Mapping Updated Successfully!',
        description: `Trade license mapping has been updated with new confidence score.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Invalidate all mapping queries to force refetch
      queryClient.invalidateQueries([getCrmBrandTradeLicenseMappingsEndpointIdentifier]);
    },
    onError: () => {
      toast({
        title: 'Update Failed',
        description: 'Something went wrong while updating the license mapping. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    ...options,
  });
}
